import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Send, Phone, Video, MoreVertical, User, Clock, CheckCircle, Paperclip, MessageCircle } from 'lucide-react';
import { useChat, ChatConversation, ChatMessage } from '../../hooks/useChat';
import { useAuth } from '../../hooks/useAuth';
import { showToast } from '../../utils/toast';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialConversationId?: string;
}

const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose, initialConversationId }) => {
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callType, setCallType] = useState<'audio' | 'video' | null>(null);
  
  const messageEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  
  const { conversations, messages, loading, sendMessage, markAsRead, startConversation } = useChat();
  const { user } = useAuth();

  // Set initial conversation if provided
  useEffect(() => {
    if (initialConversationId && conversations.some(c => c.id === initialConversationId)) {
      setActiveConversation(initialConversationId);
    }
  }, [initialConversationId, conversations]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeConversation, messages]);

  // Focus input when conversation changes
  useEffect(() => {
    if (activeConversation && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [activeConversation]);

  // Mark messages as read when conversation becomes active
  useEffect(() => {
    if (activeConversation) {
      markAsRead(activeConversation);
    }
  }, [activeConversation]);

  // Filter conversations based on search term
  const filteredConversations = conversations.filter(conv => 
    conv.participantName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSendMessage = async () => {
    if (!activeConversation || !messageText.trim()) return;
    
    try {
      await sendMessage(activeConversation, messageText);
      setMessageText('');
      
      // Maintain focus on the input after sending
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleStartCall = (type: 'audio' | 'video') => {
    if (!activeConversation) return;
    
    const conversation = conversations.find(c => c.id === activeConversation);
    if (!conversation) return;
    
    setCallType(type);
    setIsCallActive(true);
    
    // Show toast notification
    showToast.chat.callStarted(type, conversation.participantName);
    
    // Simulate call ending after 10 seconds
    setTimeout(() => {
      setIsCallActive(false);
      setCallType(null);
      showToast.chat.callEnded('00:10');
    }, 10000);
  };

  const handleStartNewChat = async (userId: string, userName: string, isExpert: boolean = false) => {
    try {
      const conversationId = await startConversation(userId, userName, isExpert);
      setActiveConversation(conversationId);
      setShowNewChat(false);
      
      // Focus the input after starting a new conversation
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle input change without losing focus
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageText(e.target.value);
  };

  // Handle key press for sending messages
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  // Call Screen Component
  const CallScreen = () => (
    <div className="absolute inset-0 bg-gray-900 z-10 flex flex-col">
      <div className="p-4 bg-gray-800 text-white flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
            {callType === 'audio' ? <Phone size={20} /> : <Video size={20} />}
          </div>
          <div>
            <p className="font-medium">
              {activeConversation && conversations.find(c => c.id === activeConversation)?.participantName}
            </p>
            <p className="text-sm text-green-400">
              {callType === 'audio' ? 'Audio Call' : 'Video Call'} • 00:10
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsCallActive(false);
            setCallType(null);
            showToast.chat.callEnded('00:10');
          }}
          className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors"
        >
          <Phone size={20} />
        </button>
      </div>
      
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <User size={48} className="text-gray-400" />
          </div>
          <p className="text-xl font-medium mb-2">
            {activeConversation && conversations.find(c => c.id === activeConversation)?.participantName}
          </p>
          <p className="text-gray-400">
            {callType === 'audio' ? 'Audio call in progress' : 'Video call in progress'}
          </p>
        </div>
      </div>
      
      <div className="p-6 bg-gray-800 flex items-center justify-center space-x-6">
        <button className="p-4 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors">
          <Paperclip size={24} className="text-white" />
        </button>
        <button className="p-4 bg-red-600 rounded-full hover:bg-red-700 transition-colors">
          <Phone size={24} className="text-white" />
        </button>
        <button className="p-4 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors">
          <Video size={24} className="text-white" />
        </button>
      </div>
    </div>
  );

  // New Chat Modal
  const NewChatModal = () => {
    const [searchUser, setSearchUser] = useState('');
    
    // Mock users for demo
    const users = [
      { id: '550e8400-e29b-41d4-a716-446655440001', name: 'Dr. Ama Osei', isExpert: true },
      { id: '550e8400-e29b-41d4-a716-446655440002', name: 'Dr. Kwaku Mensah', isExpert: true },
      { id: '550e8400-e29b-41d4-a716-446655440003', name: 'Dr. Akosua Frimpong', isExpert: true },
      { id: '550e8400-e29b-41d4-a716-446655440004', name: 'Kwame Asante', isExpert: false },
      { id: '550e8400-e29b-41d4-a716-446655440005', name: 'Ama Serwaa', isExpert: false }
    ];
    
    const filteredUsers = users.filter(u => 
      u.name.toLowerCase().includes(searchUser.toLowerCase())
    );
    
    return (
      <div className="absolute inset-0 bg-white dark:bg-gray-900 z-10 flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Conversation</h3>
          <button
            onClick={() => setShowNewChat(false)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-4">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              className="input-base pl-10 w-full"
            />
          </div>
          
          <div className="space-y-2">
            {filteredUsers.map(user => (
              <button
                key={user.id}
                onClick={() => handleStartNewChat(user.id, user.name, user.isExpert)}
                className="w-full p-3 flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <div className="avatar-md">
                  {user.name.charAt(0)}
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {user.isExpert ? 'Verified Expert' : 'Community Member'}
                  </p>
                </div>
                {user.isExpert && (
                  <span className="badge-success ml-auto">
                    Expert
                  </span>
                )}
              </button>
            ))}
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-8">
                <User className="mx-auto text-gray-400 mb-3" size={48} />
                <p className="text-gray-600 dark:text-gray-400">No users found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] overflow-hidden flex"
        onClick={e => e.stopPropagation()}
      >
        {/* Sidebar - Conversations List */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-800 flex flex-col">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Messages</h3>
            <button
              onClick={() => setShowNewChat(true)}
              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
            >
              <span className="text-xl">+</span>
            </button>
          </div>
          
          <div className="p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-base pl-9 w-full"
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto scrollbar-custom">
            {loading ? (
              <div className="p-4 text-center">
                <div className="w-6 h-6 border-2 border-green-600 dark:border-green-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Loading conversations...</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-gray-600 dark:text-gray-400">No conversations found</p>
              </div>
            ) : (
              filteredConversations.map(conversation => (
                <button
                  key={conversation.id}
                  onClick={() => {
                    setActiveConversation(conversation.id);
                    markAsRead(conversation.id);
                  }}
                  className={`w-full p-3 flex items-start space-x-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                    activeConversation === conversation.id ? 'bg-gray-100 dark:bg-gray-800' : ''
                  }`}
                >
                  <div className="relative">
                    <div className="avatar-md">
                      {conversation.participantName.charAt(0)}
                    </div>
                    {conversation.isOnline && (
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900"></div>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {conversation.participantName}
                      </p>
                      {conversation.lastMessage && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(conversation.lastMessage.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    {conversation.isExpert && (
                      <span className="badge-success text-xs">Expert</span>
                    )}
                    {conversation.lastMessage && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-1">
                        {conversation.lastMessage.senderId === user?.id ? 'You: ' : ''}
                        {conversation.lastMessage.content}
                      </p>
                    )}
                  </div>
                  {conversation.unreadCount > 0 && (
                    <div className="bg-green-600 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">
                      {conversation.unreadCount}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
        
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col relative">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="avatar-md">
                    {conversations.find(c => c.id === activeConversation)?.participantName.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {conversations.find(c => c.id === activeConversation)?.participantName}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {conversations.find(c => c.id === activeConversation)?.isExpert ? 'Verified Expert' : 'Community Member'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleStartCall('audio')}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                  >
                    <Phone size={20} />
                  </button>
                  <button
                    onClick={() => handleStartCall('video')}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                  >
                    <Video size={20} />
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => setShowOptions(!showOptions)}
                      className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                      <MoreVertical size={20} />
                    </button>
                    {showOptions && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                        <button
                          onClick={() => {
                            setShowOptions(false);
                            // View profile action
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          View Profile
                        </button>
                        <button
                          onClick={() => {
                            setShowOptions(false);
                            // Search in conversation action
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Search in Conversation
                        </button>
                        <button
                          onClick={() => {
                            setShowOptions(false);
                            // Mute notifications action
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Mute Notifications
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 p-4 overflow-y-auto scrollbar-custom bg-gray-50 dark:bg-gray-850">
                {messages[activeConversation]?.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="mx-auto text-gray-400 dark:text-gray-600 mb-3" size={48} />
                      <p className="text-gray-600 dark:text-gray-400">No messages yet</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500">Send a message to start the conversation</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages[activeConversation]?.map((message, index) => {
                      const isCurrentUser = message.senderId === user?.id;
                      const showAvatar = index === 0 || 
                        messages[activeConversation][index - 1].senderId !== message.senderId;
                      
                      return (
                        <div 
                          key={message.id} 
                          className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex items-start space-x-2 max-w-[70%] ${isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                            {!isCurrentUser && showAvatar && (
                              <div className="avatar-sm flex-shrink-0">
                                {message.senderName.charAt(0)}
                              </div>
                            )}
                            <div>
                              {!isCurrentUser && showAvatar && (
                                <div className="flex items-center mb-1">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {message.senderName}
                                  </p>
                                  {message.isExpert && (
                                    <span className="badge-success ml-2 text-xs">Expert</span>
                                  )}
                                </div>
                              )}
                              <div 
                                className={`p-3 rounded-lg ${
                                  isCurrentUser 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700'
                                }`}
                              >
                                <p>{message.content}</p>
                              </div>
                              <div className="flex items-center mt-1 space-x-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                {isCurrentUser && message.read && (
                                  <CheckCircle size={12} className="text-green-600 dark:text-green-400" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messageEndRef} />
                  </div>
                )}
              </div>
              
              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center space-x-2">
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={messageText}
                    onChange={handleMessageChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="input-base flex-1"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim()}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center p-6">
                <MessageCircle className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Your Messages</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Select a conversation or start a new one</p>
                <button
                  onClick={() => setShowNewChat(true)}
                  className="btn-primary"
                >
                  Start New Conversation
                </button>
              </div>
            </div>
          )}
          
          {/* Call Screen */}
          {isCallActive && <CallScreen />}
          
          {/* New Chat Modal */}
          {showNewChat && <NewChatModal />}
        </div>
      </div>
    </div>
  );
};

export default ChatModal;